import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import LandingFooter from '@/modules/Pages/shared/LandingFooter';
import LandingNav from '@/modules/Pages/shared/LandingNav';
import '@/modules/Pages/shared/landingPage.css';
import { AwsConfigApiGateway } from '@/utils/config/api';
import { AppRoutes } from '@/utils/routes/router';

import { assessmentMarkup } from './assessmentMarkup';
import './riskAssessment.css';

declare global {
  interface Window {
    __CHURNSIGNAL__?: {
      adminApiUrl?: string;
      apiKey?: string;
    };
  }
}

const SCRIPT_SRCS = [
  '/risk-assessment/pdf-lib.min.js',
  '/risk-assessment/pdf-report.js',
  '/risk-assessment/app.js',
] as const;

function loadScript(src: string, cacheKey: string) {
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-risk-src="${src}"]`);
    if (existing) {
      // Force a fresh run of app.js so listeners bind to the new DOM.
      if (src.endsWith('/app.js')) {
        existing.remove();
      } else {
        resolve();
        return;
      }
    }

    const script = document.createElement('script');
    script.src = src.endsWith('/app.js') ? `${src}?v=${cacheKey}` : src;
    script.async = false;
    script.dataset.riskAssessment = 'true';
    script.dataset.riskSrc = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

/**
 * Churn Risk Readiness Assessment — migrated from the standalone diagnostic site.
 * Structure and flow are preserved; colors follow the marketing theme.
 */
export default function RiskAssessmentPage() {
  const navigate = useNavigate();
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    let cancelled = false;
    const cacheKey = String(Date.now());
    window.__CHURNSIGNAL__ = {
      adminApiUrl: AwsConfigApiGateway.adminApiUrl,
      apiKey: AwsConfigApiGateway.pooledTenantApiKey,
    };
    host.innerHTML = assessmentMarkup;

    (async () => {
      try {
        for (const src of SCRIPT_SRCS) {
          if (cancelled) return;
          await loadScript(src, cacheKey);
        }
      } catch (error) {
        console.error('[RiskAssessment] failed to boot assessment scripts', error);
      }
    })();

    return () => {
      cancelled = true;
      // Keep pdf-lib / pdf-report cached; only drop the page-bound app script.
      document.querySelectorAll('script[data-risk-src$="/app.js"]').forEach((node) => node.remove());
      host.innerHTML = '';
    };
  }, []);

  return (
    <div className="landing-page landing-grain min-h-screen font-sans relative">
      <div className="relative z-10">
        <LandingNav onWaitlistClick={() => navigate(AppRoutes.waitlist)} ctaLabel="Join the waitlist" />
        <div className="pt-24">
          <div ref={hostRef} className="risk-assessment-page" />
        </div>
        <LandingFooter />
      </div>
    </div>
  );
}
